import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../lib/api';
import type { TenantSettings } from '../lib/types';
import { useToast } from './toast';
import { Button, Card, CardHeader, ErrorState, Field, Input, PageLoader, Textarea } from './ui';

/**
 * Settings → Damage billing: when invoices are due and how to pay. Posting them to Blackbaud is its own integration,
 * Blackbaud Billing Management.
 */
export function DamageBillingSettings() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const tenant = useQuery({ queryKey: ['tenant'], queryFn: () => api.get<TenantSettings>('/tenant') });
  const [dueDays, setDueDays] = useState('30');
  const [instructions, setInstructions] = useState('');

  useEffect(() => {
    if (!tenant.data) return;
    setDueDays(String(tenant.data.invoiceDueDays));
    setInstructions(tenant.data.invoicePaymentInstructions ?? '');
  }, [tenant.data]);

  const save = useMutation({
    mutationFn: () => api.patch<TenantSettings>('/tenant', { invoiceDueDays: Number(dueDays), invoicePaymentInstructions: instructions }),
    onSuccess: (saved) => {
      queryClient.setQueryData(['tenant'], saved);
      toast.success('Invoice settings saved');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (tenant.isLoading) return <PageLoader />;
  if (tenant.error) return <ErrorState error={tenant.error} onRetry={() => tenant.refetch()} />;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };

  return (
    <Card>
      <CardHeader title="Damage invoices" description="Technicians draft invoices from incidents. Administrators email them to parents, post them to Blackbaud, and record payment." />
      <form onSubmit={submit} className="max-w-2xl space-y-5 p-5">
        <Field label="Days to pay" hint="New invoices are due this many days after they’re made. Each draft’s due date can be changed.">
          <Input type="number" min={0} max={365} value={dueDays} onChange={(event) => setDueDays(event.target.value)} className="w-32" required />
        </Field>
        <Field label="How to pay" hint="Shown on every invoice and in the email to parents: where to pay online, who to make checks out to, or who to contact.">
          <Textarea rows={4} value={instructions} onChange={(event) => setInstructions(event.target.value)} maxLength={2000} />
        </Field>
        <p className="text-sm text-slate-600">
          Invoices are emailed from the help desk mailbox, and posted to families’ Blackbaud bills through Blackbaud Billing Management.
        </p>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
          <Link to="/integrations/email" className="inline-flex items-center gap-1 font-medium text-brand-700 hover:underline">
            Help desk email <ExternalLink className="size-3.5" />
          </Link>
          <Link to="/integrations/blackbaud-billing" className="inline-flex items-center gap-1 font-medium text-brand-700 hover:underline">
            Blackbaud Billing Management <ExternalLink className="size-3.5" />
          </Link>
        </div>
        <Button type="submit" variant="primary" loading={save.isPending}>
          Save
        </Button>
      </form>
    </Card>
  );
}
